import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';

import { LiveReportList } from '@futrli-sdk/predict';
import { FormattedMessage } from '@futrli/localisation';

import { DeleteDialogWizard, useDialogWizardState } from '@futrli/ui-components/components/dialog-wizard';
import { useApiView } from '@futrli/views/hooks/useApiView';
import { ModifyReportView } from '@futrli/views/views/reports/ModifyReportView/ModifyReportView';

import { useRouteIds } from '../../../hooks/useRouteIds';

interface DeleteReportDialogContainerProps {
  isOpen: boolean;
  onClose: () => void;
  report: LiveReportList;
}

export const DeleteReportDialogContainer = ({ isOpen, onClose, report }: DeleteReportDialogContainerProps) => {
  const intl = useIntl();
  const { workspaceId, organisationId, isConsolidation } = useRouteIds();

  const view = useApiView(
    useMemo(() => new ModifyReportView(workspaceId, organisationId, isConsolidation), [
      workspaceId,
      organisationId,
      isConsolidation
    ])
  );

  const { screen, handleAction: handleDelete, handleClose } = useDialogWizardState({
    isLoading: view.state.isDeleting,
    error: view.state.errorDeleting,
    onAction: () => view.deleteReport({ reportId: report.id }),
    onClose
  });

  const title = intl.formatMessage({ id: 'reporting.delete_report', defaultMessage: 'Delete report' });

  const deleteDescription = intl.formatMessage({
    id: 'reporting.confirm_delete_report_with_no_name',
    defaultMessage: 'Are you sure you want to delete this report?'
  });

  const thingsToKnow = [
    <FormattedMessage
      key="unrecoverable"
      id="reporting.delete_report_things_to_know_unrecoverable"
      defaultMessage="Deleting a report is not recoverable"
    />,
    <FormattedMessage
      key="access"
      id="reporting.delete_report_things_to_know_access"
      defaultMessage="All users will immediately lose access to this report"
    />
  ];

  const loadingMessage = intl.formatMessage({
    id: 'reporting.deleting_report',
    defaultMessage: 'Deleting report...'
  });

  const successMessage = intl.formatMessage({
    id: 'reporting.report_deleted',
    defaultMessage: 'Report deleted'
  });

  const errorMessage = view.state.errorDeleting?.message;

  return (
    <DeleteDialogWizard
      isOpen={isOpen}
      onClose={handleClose}
      onDelete={handleDelete}
      screen={screen}
      title={title}
      subtitle={report.name}
      deleteDescription={deleteDescription}
      thingsToKnow={thingsToKnow}
      loadingMessage={loadingMessage}
      successMessage={successMessage}
      errorMessage={errorMessage}
      dataTestId="delete-report-dialog"
    />
  );
};
